import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import {
  Play,
  Pause,
  Square,
  Clock,
  Calendar,
  Filter,
  Search,
  Plus,
  Edit,
  Trash2,
  Timer,
  DollarSign,
  BarChart3
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { formatCurrency, formatDate } from '@/utils'
import { timeTrackingApi, projectsApi, apiClient } from '@/services/api'

interface TimeEntry {
  id: string
  projectId: string
  projectName: string
  description: string
  startTime: string
  endTime?: string
  hours: number // backend returns hours, not duration in minutes
  hourlyRate: number
  totalCost: number
  loggedAt: string // backend uses loggedAt, not date
  project?: {
    name: string
  }
}

export const TimeTrackingPage: React.FC = () => {
  const [isTimerRunning, setIsTimerRunning] = useState(false)
  const [currentSession, setCurrentSession] = useState<{
    projectId: string
    projectName: string
    description: string
    startTime: Date
    elapsedTime: number
  } | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedProject, setSelectedProject] = useState('all')
  const [selectedPeriod, setSelectedPeriod] = useState('week')
  const [showNewEntry, setShowNewEntry] = useState(false)
  const [manualEntryForm, setManualEntryForm] = useState({
    projectId: '',
    description: '',
    hours: '',
    hourlyRate: '',
    activityType: '',
    date: new Date().toISOString().split('T')[0] // Today's date in YYYY-MM-DD format
  })
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([])
  const [projects, setProjects] = useState<Array<{id: string, name: string}>>([
    { id: 'all', name: 'All Projects' }
  ])
  const [activityTypes, setActivityTypes] = useState<Array<{
    id: string
    name: string
    description?: string
  }>>([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalHours: 0,
    totalEarnings: 0,
    avgHourlyRate: 0,
    entriesThisPeriod: 0
  })

  const periodOptions = [
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'year', label: 'This Year' }
  ]

  // Load data on component mount
  useEffect(() => {
    fetchTimeEntries()
    fetchProjects()
    fetchActivityTypes()
    fetchActiveTimer()
    fetchStatistics()
  }, [])

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout
    if (isTimerRunning && currentSession) {
      interval = setInterval(() => {
        setCurrentSession(prev => prev ? {
          ...prev,
          elapsedTime: Math.floor((Date.now() - prev.startTime.getTime()) / 1000)
        } : null)
      }, 1000)
    }
    return () => clearInterval(interval)
  }, [isTimerRunning, currentSession])

  const fetchTimeEntries = async () => {
    try {
      const response = await timeTrackingApi.getEntries()
      // Handle paginated response structure
      let entries = []
      if (response && Array.isArray(response.data)) {
        entries = response.data
      } else if (Array.isArray(response)) {
        entries = response
      }
      
      // Transform backend data to match frontend interface
      const transformedEntries = entries.map((entry: any) => {
        // Try to get project name from multiple sources
        let projectName = 'Unknown Project'
        if (entry.project?.name) {
          projectName = entry.project.name
        } else if (entry.projectId) {
          // Try to find project name from our projects list
          const project = projects.find(p => p.id === entry.projectId)
          if (project && project.id !== 'all') {
            projectName = project.name
          }
        }

        return {
          id: entry.id,
          projectId: entry.projectId,
          projectName: projectName,
          description: entry.description,
          startTime: entry.startTime,
          endTime: entry.endTime,
          hours: parseFloat(entry.hours?.toString() || '0'),
          hourlyRate: parseFloat(entry.hourlyRate?.toString() || '0'),
          totalCost: parseFloat(entry.totalCost?.toString() || '0'),
          loggedAt: entry.loggedAt || entry.createdAt || new Date().toISOString(),
          project: entry.project
        }
      })
      
      setTimeEntries(transformedEntries)
    } catch (error) {
      console.error('Error fetching time entries:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchProjects = async () => {
    try {
      const response = await projectsApi.getAll()
      // Handle paginated response structure
      let data = []
      if (response && Array.isArray(response.data)) {
        data = response.data
      } else if (Array.isArray(response)) {
        data = response
      }
      
      const projectOptions = [
        { id: 'all', name: 'All Projects' },
        ...data.map((project: any) => ({ id: project.id, name: project.name }))
      ]
      setProjects(projectOptions)
    } catch (error) {
      console.error('Error fetching projects:', error)
    }
  }

  const fetchActivityTypes = async () => {
    try {
      const response = await apiClient.get('/templates/user/activity-types')
      setActivityTypes(response.data || [])
    } catch (error) {
      console.error('Error fetching activity types:', error)
      setActivityTypes([])
    }
  }

  const fetchActiveTimer = async () => {
    try {
      const response = await timeTrackingApi.getActiveTimer()
      const activeTimer = response
      if (activeTimer) {
        setCurrentSession({
          projectId: activeTimer.projectId,
          projectName: activeTimer.project?.name || 'Unknown Project',
          description: activeTimer.description,
          startTime: new Date(activeTimer.startTime),
          elapsedTime: Math.floor((Date.now() - new Date(activeTimer.startTime).getTime()) / 1000)
        })
        setIsTimerRunning(!activeTimer.isPaused)
      }
    } catch (error) {
      console.error('Error fetching active timer:', error)
    }
  }

  const fetchStatistics = async () => {
    try {
      const response = await timeTrackingApi.getTimeStats()
      const data = response
      setStats({
        totalHours: data.totalHours || 0,
        totalEarnings: data.totalCost || 0,
        avgHourlyRate: data.averageHourlyRate || 0,
        entriesThisPeriod: data.totalEntries || 0
      })
    } catch (error) {
      console.error('Error fetching statistics:', error)
    }
  }

  const startTimer = async (projectId: string, projectName: string, description: string) => {
    try {
      await timeTrackingApi.startTimer({
        projectId,
        description
      })

      setCurrentSession({
        projectId,
        projectName,
        description,
        startTime: new Date(),
        elapsedTime: 0
      })
      setIsTimerRunning(true)
    } catch (error) {
      console.error('Error starting timer:', error)
    }
  }

  const pauseTimer = async () => {
    try {
      await apiClient.post('/time-tracking/timer/pause')
      setIsTimerRunning(false)
    } catch (error) {
      console.error('Error pausing timer:', error)
    }
  }

  const resumeTimer = async () => {
    try {
      await apiClient.post('/time-tracking/timer/resume')
      setIsTimerRunning(true)
    } catch (error) {
      console.error('Error resuming timer:', error)
    }
  }

  const stopTimer = async () => {
    try {
      await apiClient.post('/time-tracking/timer/stop')

      setCurrentSession(null)
      setIsTimerRunning(false)
      
      // Refresh data after stopping timer
      await fetchTimeEntries()
      await fetchStatistics()
    } catch (error) {
      console.error('Error stopping timer:', error)
    }
  }

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  const formatDuration = (hours: number) => {
    if (isNaN(hours) || hours === null || hours === undefined) {
      return '0h 0m'
    }
    
    const wholeHours = Math.floor(hours)
    const minutes = Math.round((hours - wholeHours) * 60)
    
    if (wholeHours > 0) {
      return `${wholeHours}h ${minutes}m`
    }
    return `${minutes}m`
  }

  const deleteTimeEntry = async (entryId: string) => {
    if (!confirm('Are you sure you want to delete this time entry?')) {
      return
    }
    
    try {
      await timeTrackingApi.deleteEntry(entryId)
      await fetchTimeEntries()
      await fetchStatistics()
    } catch (error) {
      console.error('Error deleting time entry:', error)
      alert('Failed to delete time entry. Please try again.')
    }
  }

  const createManualEntry = async () => {
    try {
      if (!manualEntryForm.projectId || !manualEntryForm.description || !manualEntryForm.hours || !manualEntryForm.activityType) {
        alert('Please fill in all required fields')
        return
      }

      const hours = parseFloat(manualEntryForm.hours)
      let hourlyRate = 25.00 // Default fallback rate
      
      // Use provided hourly rate if available
      if (manualEntryForm.hourlyRate && manualEntryForm.hourlyRate.trim() !== '') {
        hourlyRate = parseFloat(manualEntryForm.hourlyRate)
      }
      
      // Validate minimum values
      if (hours < 0.01) {
        alert('Hours must be at least 0.01')
        return
      }
      
      if (hourlyRate < 0.01) {
        alert('Hourly rate must be at least 0.01')
        return
      }
      
      // Prepare the entry data - hourlyRate is required by the DTO
      // Since loggedAt is optional in the DTO and has a default in the entity,
      // let's try omitting it and let the database set it automatically
      const entryData = {
        projectId: manualEntryForm.projectId,
        description: manualEntryForm.description,
        hours: hours,
        hourlyRate: hourlyRate,
        templateActivityTypeId: manualEntryForm.activityType
        // loggedAt omitted - let database set default CURRENT_TIMESTAMP
      }
      
      console.log('Sending manual entry data:', entryData)
      
      const response = await timeTrackingApi.createEntry(entryData)
      console.log('Manual entry created successfully:', response)

      // Reset form and close modal
      setManualEntryForm({
        projectId: '',
        description: '',
        hours: '',
        hourlyRate: '',
        activityType: '',
        date: new Date().toISOString().split('T')[0]
      })
      setShowNewEntry(false)

      // Refresh data
      await fetchTimeEntries()
      await fetchStatistics()
      
      alert('Time entry created successfully!')
    } catch (error: any) {
      console.error('Error creating manual entry:', error)
      console.error('Error response:', error.response)
      console.error('Error response data:', error.response?.data)
      console.error('Validation messages:', error.response?.data?.message)
      
      // Show more detailed error message
      let errorMessage = 'Failed to create time entry'
      if (error.response?.data?.message) {
        if (Array.isArray(error.response.data.message)) {
          errorMessage = error.response.data.message.join(', ')
        } else {
          errorMessage = error.response.data.message
        }
      } else if (error.message) {
        errorMessage = error.message
      }
      
      alert(`Failed to create time entry: ${errorMessage}`)
    }
  }

  // Filter time entries based on search and filters
  const filteredEntries = timeEntries.filter(entry => {
    const matchesSearch = entry.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         entry.projectName.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesProject = selectedProject === 'all' || entry.projectId === selectedProject
    return matchesSearch && matchesProject
  })

  return (
    <>
      <Helmet>
        <title>Time Tracking - Jewelry Inventory Tracker</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Time Tracking
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Track time spent on jewelry projects and calculate labor costs
            </p>
          </div>
          <Button
            onClick={() => setShowNewEntry(true)}
            leftIcon={<Plus className="w-4 h-4" />}
            className="bg-blue-600 hover:bg-blue-700 text-white border-blue-600 font-medium shadow-md"
          >
            Manual Entry
          </Button>
        </div>

        {/* Active Timer */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Timer className="w-5 h-5" />
              Active Timer
            </h2>
            {currentSession && (
              <Badge variant="primary" className="animate-pulse">
                Recording
              </Badge>
            )}
          </div>

          {currentSession ? (
            <div className="space-y-4">
              <div className="text-center">
                <div className="text-4xl font-mono font-bold text-gray-900 dark:text-white mb-2">
                  {formatTime(currentSession.elapsedTime)}
                </div>
                <div className="text-lg text-gray-600 dark:text-gray-400">
                  {currentSession.projectName}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-500">
                  {currentSession.description}
                </div>
              </div>
              
              <div className="flex justify-center gap-3">
                {isTimerRunning ? (
                  <Button
                    onClick={pauseTimer}
                    variant="outline"
                    leftIcon={<Pause className="w-4 h-4" />}
                  >
                    Pause
                  </Button>
                ) : (
                  <Button
                    onClick={resumeTimer}
                    leftIcon={<Play className="w-4 h-4" />}
                  >
                    Resume
                  </Button>
                )}
                <Button
                  onClick={stopTimer}
                  variant="outline"
                  leftIcon={<Square className="w-4 h-4" />}
                  className="text-red-600 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-600 dark:hover:bg-red-900/20"
                >
                  Stop & Save
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Clock className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Active Timer
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Start tracking time on a project to monitor your work hours and calculate costs.
              </p>
              
              {/* Quick Start */}
              <div className="max-w-md mx-auto space-y-3">
                <select
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                  onChange={(e) => {
                    const projectId = e.target.value
                    const project = projects.find(p => p.id === projectId)
                    if (project && projectId !== 'all') {
                      const description = prompt('Enter task description:')
                      if (description) {
                        startTimer(projectId, project.name, description)
                      }
                    }
                  }}
                  value=""
                >
                  <option value="">Select a project to start timer...</option>
                  {projects.filter(p => p.id !== 'all').map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Total Hours ({selectedPeriod})
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                  {stats.totalHours.toFixed(1)}h
                </p>
              </div>
              <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/20">
                <Clock className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Total Earnings
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                  {formatCurrency(stats.totalEarnings)}
                </p>
              </div>
              <div className="p-3 rounded-full bg-green-100 dark:bg-green-900/20">
                <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Avg Hourly Rate
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                  {formatCurrency(stats.avgHourlyRate)}
                </p>
              </div>
              <div className="p-3 rounded-full bg-purple-100 dark:bg-purple-900/20">
                <BarChart3 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Time Entries
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                  {stats.entriesThisPeriod}
                </p>
              </div>
              <div className="p-3 rounded-full bg-orange-100 dark:bg-orange-900/20">
                <Calendar className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search time entries..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                />
              </div>
            </div>

            {/* Project Filter */}
            <div className="w-full lg:w-48">
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
              >
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Period Filter */}
            <div className="w-full lg:w-48">
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
              >
                {periodOptions.map((period) => (
                  <option key={period.value} value={period.value}>
                    {period.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Time Entries */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          {loading ? (
            <div className="text-center py-12">
              <Clock className="mx-auto h-12 w-12 text-gray-400 animate-pulse mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Loading time entries...</p>
            </div>
          ) : filteredEntries.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No time entries found
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Start your first timer or add a manual entry to begin tracking your jewelry making time.
              </p>
              <div className="flex justify-center gap-3">
                <Button
                  onClick={() => {
                    const projectId = projects.find(p => p.id !== 'all')?.id
                    const projectName = projects.find(p => p.id !== 'all')?.name
                    if (projectId && projectName) {
                      const description = prompt('Enter task description:')
                      if (description) {
                        startTimer(projectId, projectName, description)
                      }
                    }
                  }}
                  leftIcon={<Play className="w-4 h-4" />}
                >
                  Start Timer
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowNewEntry(true)}
                  leftIcon={<Plus className="w-4 h-4" />}
                >
                  Manual Entry
                </Button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">
                      Date
                    </th>
                    <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">
                      Project
                    </th>
                    <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">
                      Description
                    </th>
                    <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">
                      Duration
                    </th>
                    <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">
                      Rate
                    </th>
                    <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">
                      Total
                    </th>
                    <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEntries.map((entry) => (
                    <tr
                      key={entry.id}
                      className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                      <td className="py-4 px-6 text-gray-900 dark:text-white">
                        {formatDate(entry.loggedAt)}
                      </td>
                      <td className="py-4 px-6 text-gray-900 dark:text-white">
                        {entry.projectName}
                      </td>
                      <td className="py-4 px-6 text-gray-900 dark:text-white">
                        {entry.description}
                      </td>
                      <td className="py-4 px-6 text-gray-900 dark:text-white">
                        {formatDuration(entry.hours)}
                      </td>
                      <td className="py-4 px-6 text-gray-900 dark:text-white">
                        {formatCurrency(entry.hourlyRate)}/hr
                      </td>
                      <td className="py-4 px-6 text-gray-900 dark:text-white">
                        {formatCurrency(entry.totalCost)}
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              // TODO: Implement edit functionality
                              alert('Edit functionality coming soon!')
                            }}
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteTimeEntry(entry.id)}
                            className="text-red-600 hover:text-red-700 hover:border-red-300"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Manual Entry Modal */}
        {showNewEntry && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Add Manual Time Entry
                </h3>
                <button
                  onClick={() => setShowNewEntry(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                {/* Project Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Project *
                  </label>
                  <select
                    value={manualEntryForm.projectId}
                    onChange={(e) => setManualEntryForm(prev => ({ ...prev, projectId: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                    required
                  >
                    <option value="">Select a project...</option>
                    {projects.filter(p => p.id !== 'all').map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description *
                  </label>
                  <input
                    type="text"
                    value={manualEntryForm.description}
                    onChange={(e) => setManualEntryForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                    placeholder="What did you work on?"
                    required
                  />
                </div>

                {/* Activity Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Activity Type *
                  </label>
                  <select
                    value={manualEntryForm.activityType}
                    onChange={(e) => setManualEntryForm(prev => ({ ...prev, activityType: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                    required
                  >
                    <option value="">Select activity type...</option>
                    {activityTypes.map((activity) => (
                      <option key={activity.id} value={activity.id}>
                        {activity.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Hours */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Hours *
                  </label>
                  <input
                    type="number"
                    step="0.25"
                    min="0"
                    value={manualEntryForm.hours}
                    onChange={(e) => setManualEntryForm(prev => ({ ...prev, hours: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                    placeholder="2.5"
                    required
                  />
                </div>

                {/* Hourly Rate */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Hourly Rate ($/hr)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={manualEntryForm.hourlyRate}
                    onChange={(e) => setManualEntryForm(prev => ({ ...prev, hourlyRate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                    placeholder="Leave empty to use your default rate"
                  />
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Date
                  </label>
                  <input
                    type="date"
                    value={manualEntryForm.date}
                    onChange={(e) => setManualEntryForm(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowNewEntry(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={createManualEntry}
                  leftIcon={<Plus className="w-4 h-4" />}
                >
                  Add Entry
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}